import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImageWithFallback from './ImageWithFallback';
import { renderTextWithLinks } from '../utils/text';
import UserLink from './UserLink';
import {
  createGroupPostComment,
  getUserMe,
  listGroupPostComments,
  toggleGroupPostCommentLike,
} from '../utils/secureFunctions';

interface CommentDoc {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  content: string;
  likes?: string[];
  parentId?: string | null;
  createdAt?: string | Date;
}

interface Props {
  contentId: string; // groupPost id
  contentOwnerId?: string;
  className?: string;
  style?: React.CSSProperties;
}

const GroupComments: React.FC<Props> = ({ contentId, className, style }) => {
  const [roots, setRoots] = useState<CommentDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [profile, setProfile] = useState<{ name: string; photoURL: string }>({ name: '', photoURL: '' });

  const [repliesOpen, setRepliesOpen] = useState<Record<string, boolean>>({});
  const [repliesLoading, setRepliesLoading] = useState<Record<string, boolean>>({});
  const [replies, setReplies] = useState<Record<string, CommentDoc[]>>({});

  const formatTime = useCallback((value?: string | Date) => {
    if (!value) return '';
    const date = typeof value === 'string' ? new Date(value) : value;
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
  }, []);

  const sortedRoots = useMemo(() => {
    return [...roots].sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [roots]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUserMe();
        if (cancelled) return;
        const p = res.profile || ({} as any);
        setProfile({ name: p.name || p.username || 'User', photoURL: p.photoURL || '' });
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRoots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listGroupPostComments(contentId, null);
      setRoots(res.comments || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [contentId]);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  const loadReplies = useCallback(async (rootId: string) => {
    setRepliesLoading((prev) => ({ ...prev, [rootId]: true }));
    try {
      const res = await listGroupPostComments(contentId, rootId);
      setReplies((prev) => ({ ...prev, [rootId]: res.comments || [] }));
      setRepliesOpen((prev) => ({ ...prev, [rootId]: true }));
    } finally {
      setRepliesLoading((prev) => ({ ...prev, [rootId]: false }));
    }
  }, [contentId]);

  const toggleReplies = useCallback((rootId: string) => {
    const open = repliesOpen[rootId];
    if (open) {
      setRepliesOpen((prev) => ({ ...prev, [rootId]: false }));
      return;
    }
    if (!replies[rootId]) {
      loadReplies(rootId);
    } else {
      setRepliesOpen((prev) => ({ ...prev, [rootId]: true }));
    }
  }, [loadReplies, replies, repliesOpen]);

  const handlePost = useCallback(async () => {
    const content = newComment.trim();
    if (!content) return;
    setPosting(true);
    try {
      const res = await createGroupPostComment(contentId, {
        content,
        userName: profile.name || 'User',
        userPhoto: profile.photoURL || null,
      });
      setNewComment('');
      const comment = res.comment as CommentDoc;
      setRoots((prev) => [comment, ...prev]);
    } finally {
      setPosting(false);
    }
  }, [contentId, newComment, profile.name, profile.photoURL]);

  const handlePostReply = useCallback(async (rootId: string) => {
    const content = replyText.trim();
    if (!content) return;
    setPosting(true);
    try {
      const res = await createGroupPostComment(contentId, {
        content,
        parentId: rootId,
        userName: profile.name || 'User',
        userPhoto: profile.photoURL || null,
      });
      setReplyText('');
      setReplyingTo(null);
      const comment = res.comment as CommentDoc;
      setReplies((prev) => ({
        ...prev,
        [rootId]: [...(prev[rootId] || []), comment],
      }));
      setRepliesOpen((prev) => ({ ...prev, [rootId]: true }));
    } finally {
      setPosting(false);
    }
  }, [contentId, profile.name, profile.photoURL, replyText]);

  const handleToggleLike = useCallback(async (comment: CommentDoc) => {
    try {
      const res = await toggleGroupPostCommentLike(comment.id);
      const updated = res.comment as CommentDoc;
      setRoots((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setReplies((prev) => {
        const next: Record<string, CommentDoc[]> = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = next[key].map((c) => (c.id === updated.id ? updated : c));
        });
        return next;
      });
    } catch {}
  }, []);

  return (
    <div className={`group-comments ${className || ''}`} style={style}>
      <div className="group-comments-header">
        <h3>Comments</h3>
      </div>

      <div className="group-comments-compose">
        <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Write a comment..." />
        <button onClick={handlePost} disabled={posting}>{posting ? 'Posting...' : 'Post Comment'}</button>
      </div>

      {loading && <div className="loading">Loading comments...</div>}
      {error && <div className="error">{error}</div>}
      {!loading && !sortedRoots.length && <div className="empty">No comments yet.</div>}

      <div className="group-comments-list">
        {sortedRoots.map((comment) => (
          <div key={comment.id} className="group-comment-item">
            <div className="group-comment-avatar">
              <ImageWithFallback src={comment.userPhoto || ''} alt={comment.userName} />
            </div>
            <div className="group-comment-body">
              <div className="group-comment-header">
                <UserLink userId={comment.userId} className="name">{comment.userName || 'User'}</UserLink>
                <span className="time">{formatTime(comment.createdAt)}</span>
              </div>
              <div className="group-comment-content">
                <div className="comment-text">{renderTextWithLinks(comment.content || '')}</div>
              </div>
              <div className="group-comment-actions">
                <button onClick={() => handleToggleLike(comment)}>Like ({comment.likes?.length || 0})</button>
                <button onClick={() => setReplyingTo(comment.id)}>Reply</button>
                <button onClick={() => toggleReplies(comment.id)}>
                  {repliesOpen[comment.id] ? 'Hide replies' : 'View replies'} ({replies[comment.id]?.length || 0})
                </button>
              </div>

              {replyingTo === comment.id && (
                <div className="reply-box">
                  <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply..." />
                  <button onClick={() => handlePostReply(comment.id)} disabled={posting}>{posting ? 'Posting...' : 'Post Reply'}</button>
                </div>
              )}

              {repliesOpen[comment.id] && (
                <div className="replies">
                  {repliesLoading[comment.id] && <div className="loading">Loading replies...</div>}
                  {(replies[comment.id] || []).map((reply) => (
                    <div key={reply.id} className="group-comment-item reply">
                      <div className="group-comment-avatar">
                        <ImageWithFallback src={reply.userPhoto || ''} alt={reply.userName} />
                      </div>
                      <div className="group-comment-body">
                        <div className="group-comment-header">
                          <UserLink userId={reply.userId} className="name">{reply.userName || 'User'}</UserLink>
                          <span className="time">{formatTime(reply.createdAt)}</span>
                        </div>
                        <div className="group-comment-content">
                          <div className="comment-text">{renderTextWithLinks(reply.content || '')}</div>
                        </div>
                        <div className="group-comment-actions">
                          <button onClick={() => handleToggleLike(reply)}>Like ({reply.likes?.length || 0})</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GroupComments;
